import { useContext, useEffect, useRef, useState, useCallback } from "react"
import { Wrapper, Page, Fade, Hyper, Media } from "components"
import { imageService } from "utils"
import styles from "./styles.module.scss"
import { render } from "storyblok-rich-text-react-renderer"
import * as Counters from "./Counters"
import cn from 'clsx';

import type { FC, HTMLAttributes } from "react"

interface SectionProps
extends HTMLAttributes<HTMLDivElement> {}

const sizes = [175, 350, 700]

export const Section: FC<SectionProps> = ({ children, ...props }) => {
  const { content } = useContext(Page.Context)
  const [ activeIndex, setIndex ] = useState(0);
  const scrollableRef = useRef<HTMLUListElement>(null)
  const clickHandler = useCallback((modifier: number) => () => {
    const { clientWidth } = scrollableRef.current.children[0];
    scrollableRef.current.scrollTo({
      left: clientWidth * (activeIndex + modifier),
      behavior: 'smooth',
    })
  }, [activeIndex])

  useEffect(() => {
    scrollableRef.current!.addEventListener('scroll', function() {

      for (let i = 0; i < this.children.length; i++) {
        const { left, width } = this.children[i].getBoundingClientRect()
        if (left > 0 && (left + width) < window.screen.availWidth) 
          return setIndex(i)
      }
    })
  }, [])

  return (
    <Page.Section.Container id="services" {...props}>
      <Fade>
        <Wrapper double>
          <Page.Section.Heading size="small">
            {content.servicesHeading}
          </Page.Section.Heading>
          <Page.Section.Description content={content.servicesDescription} />
          <Counters.Container>
            {(content.countersList ?? []).map((counter, index) => (
              <Counters.Item key={index} {...counter} />
            ))}
          </Counters.Container>
          <div className={styles.features}>
            <div className={styles.header}>
              <div className={styles.controls}>
                <button onClick={clickHandler(-1)}>
                  ← PREV
                </button>
                <button onClick={clickHandler(+1)}>
                  NEXT →
                </button>
              </div>
              <Media from="tablet">
                <Hyper.Link href="#projects">
                  Best projects
                </Hyper.Link>
              </Media>
            </div>
            <ul className={styles.scrollable} ref={scrollableRef}>
              {(content.serviceList ?? []).map((service, index) => (
                <li className={cn(styles.service, {
                  [styles.active]: activeIndex === index,
                })} key={index}>
                  <picture className={styles.screenshot}>
                    <source type="image/webp" srcSet={sizes.map(width => [
                      imageService(service.image.filename, { resize: { width }, filter: { format: 'webp' } }),
                      `${width}w`
                    ].join(' ')).join(', ')} sizes="(min-width: 700px) 700px, 100vw" />
                    <img sizes="(min-width: 700px) 700px, 100vw" decoding="async"
                      src={service.image.filename}
                      srcSet={sizes.map(width => [
                        imageService(service.image.filename, { resize: { width }, filter: { format: 'jpeg' } }),
                        `${width}w`
                      ].join(' ')).join(', ')}
                      alt="Screenshot" />
                  </picture>
                  <p className={styles.heading}>
                    {service.title}
                  </p>
                  <div className={styles.description}>
                    {render(service.description)}
                  </div>
                </li>
              ))}
            </ul>
          </div>
          <div className={styles.carousel}>

          </div>
        </Wrapper>
      </Fade>
    </Page.Section.Container>
  )
}
